import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'react-toastify';
import AttachIcon from '../../../../assets/icons/AttachIcon';
import { createContentSubmission, getContentSubmissionPresignedUrl } from '../../../../services/dashboard.service';
import { MAX_IMAGES } from '../../../../utils/constants';
import { uploadFilesToS3 } from '../../../../utils/s3-upload-utils';
import ButtonComponent from '../../../common/button-component/button-component';
import CustomModal from '../../../common/custom-modal/custom-modal';
import CustomTooltip from '../../../common/CustomTooltip/CustomTooltip';
import InputComponent from '../../../common/input-component/input-component';
import SlidingButton from '../../../common/SlidingButton/SlidingButton';
import UploadComponent from '../upload-component/upload-component';
import { validateAndFilterFiles } from '../upload-component/upload-utils';
import styles from './upload-modal.module.css';

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const sliderOptions = [{ value: 'Media' }, { value: 'Link' }];

export default function UploadModal({ isOpen, onClose }: UploadModalProps) {
  const [contentName, setContentName] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [selectedOption, setSelectedOption] = useState('Media');
  const [linkUrl, setLinkUrl] = useState('');
  const [hasVideo, setHasVideo] = useState(false);
  const queryClient = useQueryClient();

  const handleClose = () => {
    setContentName('');
    setSelectedFiles([]);
    setSelectedOption('Media');
    setLinkUrl('');
    setHasVideo(false);
    onClose();
  };

  // Mutation for getting presigned URLs and uploading files
  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (selectedOption === 'Media') {
        if (!selectedFiles.length) throw new Error('No files selected');

        // Get presigned URLs
        const fileNames = selectedFiles.map((file) => file.name);
        const presignedUrlResponse = await getContentSubmissionPresignedUrl(fileNames);

        if (!presignedUrlResponse.status) {
          throw new Error(presignedUrlResponse.message || 'Failed to get upload URLs');
        }

        const { urls } = presignedUrlResponse.data;

        // Upload files to presigned URLs
        const uploadedFiles = await uploadFilesToS3(selectedFiles, urls);

        // Create content submission for media
        const createResponse = await createContentSubmission({
          type: 'media',
          name: contentName,
          keys: uploadedFiles,
          link: null,
        });

        if (!createResponse.status) {
          throw new Error(createResponse.message || 'Failed to create content submission');
        }

        return createResponse;
      }

      // Create content submission for link
      const createResponse = await createContentSubmission({
        type: 'link',
        name: contentName,
        keys: null,
        link: linkUrl,
      });

      if (!createResponse.status) {
        throw new Error(createResponse.message || 'Failed to create content submission');
      }

      return createResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['submissions'] });
      toast.success('Content uploaded successfully');
      handleClose();
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Error uploading content');
    },
  });

  const handleFileSelection = (files: File[]) => {
    const { validFiles, newHasVideo } = validateAndFilterFiles(files, hasVideo, selectedFiles);

    if (validFiles.length === 0) return;

    if (newHasVideo) {
      setHasVideo(true);
      setSelectedFiles(validFiles); // Replace existing files with the video
      return;
    }

    // Handle image-only uploads
    if (selectedFiles.length + validFiles.length > MAX_IMAGES) {
      toast.warning(`You can upload only ${MAX_IMAGES} images. Some files were not added.`);
      const remainingSlots = MAX_IMAGES - selectedFiles.length;
      setSelectedFiles((prev) => [...prev, ...validFiles.slice(0, remainingSlots)]);
    } else {
      setSelectedFiles((prev) => [...prev, ...validFiles]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedOption === 'Media' && contentName && selectedFiles.length > 0) {
      uploadMutation.mutate();
    } else if (selectedOption === 'Link' && contentName && linkUrl) {
      uploadMutation.mutate();
    }
  };

  const removeFile = (index: number) => {
    const fileToRemove = selectedFiles[index];
    if (fileToRemove.type.startsWith('video/')) {
      setHasVideo(false);
    }
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  if (!isOpen) return null;

  return (
    <CustomModal
      show={isOpen}
      onClose={handleClose}
      title="Submit Content"
      centeredTitle="center"
      showDivider
      width={528}
    >
      <div className={styles.modalContent}>
        <SlidingButton
          options={sliderOptions}
          selected={selectedOption}
          setSelected={setSelectedOption}
          fontSize="16px"
        />

        <form onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <InputComponent
              id="contentName"
              label="Content Name"
              type="text"
              value={contentName}
              onChange={(e) => setContentName(e.target.value)}
              placeholder="Enter Content Name"
              required
            />
          </div>

          {selectedOption === 'Media' ? (
            <UploadComponent
              selectedFiles={selectedFiles}
              onFileSelection={handleFileSelection}
              hasVideo={hasVideo}
              maxImages={MAX_IMAGES}
              removeFile={removeFile}
            />
          ) : (
            <div className={styles.formGroup}>
              <CustomTooltip title={linkUrl} show={!!linkUrl}>
                <InputComponent
                  id="linkUrl"
                  type="url"
                  left={<AttachIcon />}
                  label="Add Link"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="Paste link here..."
                  required
                />
              </CustomTooltip>
            </div>
          )}

          <div className={styles.modalActions}>
            <ButtonComponent type="button" variant="secondary" style={{ width: '100%' }} onClick={handleClose}>
              Cancel
            </ButtonComponent>
            <CustomTooltip show={Boolean(!contentName)} title="Add content name to submit">
              <ButtonComponent
                type="submit"
                variant="primary"
                style={{ width: '100%' }}
                disabled={
                  !contentName ||
                  (selectedOption === 'Media' && selectedFiles.length === 0) ||
                  (selectedOption === 'Link' && !linkUrl)
                }
                showLoading={uploadMutation.isPending}
              >
                Submit
              </ButtonComponent>
            </CustomTooltip>
          </div>
        </form>
      </div>
    </CustomModal>
  );
}
